import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Radio,
  Divider,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  MonetizationOn as MonetizationOnIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";

// RTK Query
import {
  useLazyBuscarFacturasQuery,
  useAplicarAbonoMutation,
} from "src/services/conciliacionApi";

// Helpers
import {
  formatearMonto,
  formatearRutDisplay,
  getBancoConfig,
} from "src/utils/conciliacionHelpers";

// Snackbar
import { useSnackbar } from "src/components/snackbar";

// Styles
import { makeStyles } from "@material-ui/styles";
import { formatearFecha, formatearFechaDisplay } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: "#FF9800",
    color: "#fff",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: "#fff",
  },
  infoCard: {
    backgroundColor: "#FFF3E0",
    marginBottom: theme.spacing(2),
  },
  searchCard: {
    backgroundColor: "#f5f5f5",
    marginBottom: theme.spacing(2),
  },
  facturaRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedRow: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
  },
}));

// ==================== COMPONENTE PRINCIPAL ====================

export default function ModalAplicarAbono({ open, onClose, abono }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // ==================== ESTADOS ====================

  const [criteriosBusqueda, setCriteriosBusqueda] = useState({
    folio: "",
    rut_cliente: "",
    tipo_factura: "",
    fecha_desde: null,
    fecha_hasta: null,
    estado: "",
  });

  console.log("Criterios busqueda: ", criteriosBusqueda);

  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [montoAplicar, setMontoAplicar] = useState("");
  const [observaciones, setObservaciones] = useState("");

  // ==================== RTK QUERY ====================

  const [buscarFacturas, { data: resultadosBusqueda, isLoading: buscando }] =
    useLazyBuscarFacturasQuery();

  const [aplicarAbono, { isLoading: aplicando }] = useAplicarAbonoMutation();

  // ==================== EFECTOS ====================

  // Resetear estado al abrir/cerrar
  useEffect(() => {
    if (open) {
      const filtrosSugeridos = {
        folio: "",
        rut_cliente: abono?.movimiento?.rut_ordenante || "",
        fecha_desde: null,
        fecha_hasta: null,
        tipo_factura: "",
        estado: "",
      };
      setCriteriosBusqueda(filtrosSugeridos);

      // Ejecutar búsqueda automática
      const params = {};
      if (filtrosSugeridos.rut_cliente)
        params.rut_cliente = filtrosSugeridos.rut_cliente;
      if (filtrosSugeridos.monto_min)
        params.monto_min = filtrosSugeridos.monto_min;
      if (filtrosSugeridos.monto_max)
        params.monto_max = filtrosSugeridos.monto_max;
      if (filtrosSugeridos.estado) params.estado = filtrosSugeridos.estado;

      buscarFacturas(params);

      setFacturaSeleccionada(null);
      setMontoAplicar("");
      setObservaciones("");
    }
  }, [abono?.movimiento?.rut_ordenante, buscarFacturas, open]);

  // Auto-completar monto cuando se selecciona factura
  useEffect(() => {
    if (facturaSeleccionada && abono) {
      const deudaFactura = parseFloat(facturaSeleccionada.deuda_restante || 0);
      const abonoDisponible = parseFloat(abono.monto_abono_pendiente || 0);

      // Aplicar el menor entre la deuda y el abono disponible
      const montoSugerido = Math.min(deudaFactura, abonoDisponible);
      setMontoAplicar(montoSugerido.toString());
    }
  }, [facturaSeleccionada, abono]);

  // ==================== HELPERS ====================

  const movimiento = abono?.movimiento || {};
  const bancoConfig = getBancoConfig(movimiento.banco);

  const facturas = resultadosBusqueda?.data || [];

  const montoAplicarNum = parseFloat(montoAplicar) || 0;
  const abonoDisponible = parseFloat(abono?.monto_abono_pendiente || 0);
  const deudaFactura = parseFloat(facturaSeleccionada?.deuda_restante || 0);

  const montoValido =
    montoAplicarNum > 0 &&
    montoAplicarNum <= abonoDisponible &&
    montoAplicarNum <= deudaFactura;

  const puedeAplicar = facturaSeleccionada && montoValido;

  // ==================== HANDLERS ====================

  const handleChangeFiltro = (campo, valor) => {
    setCriteriosBusqueda((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleBuscar = async () => {
    const params = {};

    if (criteriosBusqueda.rut_cliente)
      params.rut_cliente = criteriosBusqueda.rut_cliente;
    if (criteriosBusqueda.fecha_desde)
      params.fecha_desde = formatearFecha(
        criteriosBusqueda.fecha_desde,
        "YYYY-MM-DD",
      );
    if (criteriosBusqueda.fecha_hasta)
      params.fecha_hasta = formatearFecha(
        criteriosBusqueda.fecha_hasta,
        "YYYY-MM-DD",
      );
    if (criteriosBusqueda.folio) params.folio = criteriosBusqueda.folio;
    if (criteriosBusqueda.estado) params.estado = criteriosBusqueda.estado;
    if (criteriosBusqueda.tipo_factura)
      params.tipo = criteriosBusqueda.tipo_factura;

    try {
      await buscarFacturas(params).unwrap();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al buscar facturas", {
        variant: "error",
      });
    }
  };

  const handleSeleccionarFactura = (factura) => {
    setFacturaSeleccionada(factura);
  };

  const handleAplicar = async () => {
    if (!puedeAplicar) return;

    try {
      const resultado = await aplicarAbono({
        conciliacion_id: abono.id,
        factura_tipo: facturaSeleccionada.tipo,
        factura_id: facturaSeleccionada.id,
        monto_aplicar: montoAplicarNum,
        observaciones: observaciones || undefined,
      }).unwrap();

      enqueueSnackbar(resultado.mensaje || "Abono aplicado exitosamente", {
        variant: "success",
      });

      onClose();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al aplicar abono", {
        variant: "error",
      });
    }
  };

  const handleClose = () => {
    if (!aplicando) {
      onClose();
    }
  };

  // ==================== VALIDACIÓN SI NO HAY ABONO ====================

  if (!abono) {
    return null;
  }

  // ==================== RENDER ====================

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* ==================== HEADER ==================== */}
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <MonetizationOnIcon />
          <Typography variant="h6">Aplicar Abono a Factura</Typography>
        </Box>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ==================== CONTENIDO ==================== */}
      <DialogContent dividers>
        {/* INFO DEL ABONO */}
        <Card className={classes.infoCard}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              📊 Información del Abono
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Fecha Conciliación:
                </Typography>
                <Typography variant="body2">
                  {formatearFechaDisplay(abono.fecha_concilia)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Banco:
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={bancoConfig.label}
                    size="small"
                    style={{
                      backgroundColor: bancoConfig.bgColor,
                      color: bancoConfig.color || "#000",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  RUT Ordenante:
                </Typography>
                <Typography variant="body2">
                  {formatearRutDisplay(movimiento.rut_ordenante)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  Monto Movimiento:
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {formatearMonto(movimiento.monto)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: "8px 0" }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Abono Disponible:
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: "#FF9800", fontWeight: 700 }}
                >
                  {formatearMonto(abono.monto_abono_pendiente)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* BÚSQUEDA DE FACTURAS */}
        <Card className={classes.searchCard}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              🔍 Buscar Factura para Aplicar Abono
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Folio"
                  value={criteriosBusqueda.folio}
                  onChange={(e) =>
                    setCriteriosBusqueda((prev) => ({
                      ...prev,
                      folio: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="RUT Cliente"
                  value={criteriosBusqueda.rut_cliente}
                  onChange={(e) =>
                    setCriteriosBusqueda((prev) => ({
                      ...prev,
                      rut_cliente: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo Factura</InputLabel>
                  <Select
                    value={criteriosBusqueda.tipo_factura}
                    onChange={(e) =>
                      setCriteriosBusqueda((prev) => ({
                        ...prev,
                        tipo_factura: e.target.value,
                      }))
                    }
                    label="Tipo Factura"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="PLAN">Planes</MenuItem>
                    <MenuItem value="TAREA">Tareas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={criteriosBusqueda.estado}
                    onChange={(e) =>
                      handleChangeFiltro("estado", e.target.value)
                    }
                    label="Estado"
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    <MenuItem value="FACTURADO">Facturado</MenuItem>
                    <MenuItem value="ABONO">Abono</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DatePicker
                    label="Fecha Desde"
                    value={criteriosBusqueda.fecha_desde}
                    onChange={(newValue) =>
                      setCriteriosBusqueda((prev) => ({
                        ...prev,
                        fecha_desde: newValue,
                      }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DatePicker
                    label="Fecha Hasta"
                    value={criteriosBusqueda.fecha_hasta}
                    onChange={(newValue) =>
                      setCriteriosBusqueda((prev) => ({
                        ...prev,
                        fecha_hasta: newValue,
                      }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleBuscar}
                    disabled={buscando}
                  >
                    Buscar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* RESULTADOS DE BÚSQUEDA */}
        {buscando && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {!buscando && facturas.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Resultados ({facturas.length})
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Folio</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Deuda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((factura) => {
                    const isSelected =
                      facturaSeleccionada?.id === factura.id &&
                      facturaSeleccionada?.tipo === factura.tipo;

                    return (
                      <TableRow
                        key={`${factura.tipo}-${factura.id}`}
                        onClick={() => handleSeleccionarFactura(factura)}
                        className={`${classes.facturaRow} ${
                          isSelected ? classes.selectedRow : ""
                        }`}
                      >
                        <TableCell padding="checkbox">
                          <Radio checked={isSelected} />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={factura.tipo}
                            size="small"
                            color={
                              factura.tipo === "PLAN" ? "primary" : "secondary"
                            }
                          />
                        </TableCell>
                        <TableCell>{factura.folio}</TableCell>
                        <TableCell>{factura.nombre_cliente}</TableCell>
                        <TableCell>
                          <Chip
                            label={factura.estado}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600 }}
                          >
                            {formatearMonto(factura.deuda_restante)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {!buscando && resultadosBusqueda && facturas.length === 0 && (
          <Alert severity="info">
            No se encontraron facturas con los criterios especificados
          </Alert>
        )}

        {/* DETALLES DE APLICACIÓN */}
        {facturaSeleccionada && (
          <Box mt={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  ✏️ Detalles de Aplicación
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Factura seleccionada:{" "}
                      <strong>{facturaSeleccionada.folio}</strong> -{" "}
                      {facturaSeleccionada.nombre_cliente}
                    </Alert>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="textSecondary">
                      Deuda de la Factura:
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {formatearMonto(facturaSeleccionada.deuda_restante)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="textSecondary">
                      Abono Disponible:
                    </Typography>
                    <Typography
                      variant="body1"
                      style={{ fontWeight: 600, color: "#FF9800" }}
                    >
                      {formatearMonto(abonoDisponible)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Monto a Aplicar"
                      type="number"
                      value={montoAplicar}
                      onChange={(e) => setMontoAplicar(e.target.value)}
                      error={!montoValido && montoAplicar !== ""}
                      helperText={
                        !montoValido && montoAplicar !== ""
                          ? "Monto inválido"
                          : `Máx: ${formatearMonto(
                              Math.min(deudaFactura, abonoDisponible),
                            )}`
                      }
                      InputProps={{
                        startAdornment: <Typography>$</Typography>,
                      }}
                      inputProps={{
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Observaciones (opcional)"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                    />
                  </Grid>

                  {/* Validaciones */}
                  {montoAplicarNum > abonoDisponible && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        El monto supera el abono disponible
                      </Alert>
                    </Grid>
                  )}

                  {montoAplicarNum > deudaFactura && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        El monto supera la deuda de la factura
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      {/* ==================== ACCIONES ==================== */}
      <DialogActions>
        <Button onClick={handleClose} disabled={aplicando}>
          Cancelar
        </Button>

        <LoadingButton
          variant="contained"
          color="primary"
          startIcon={<CheckCircleIcon />}
          onClick={handleAplicar}
          loading={aplicando}
          disabled={!puedeAplicar}
        >
          Aplicar Abono
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
